'use client';
import React from 'react';
import Link from 'next/link';
import Avatar from '@/componentv2/avatar/avatar';
import {
  BadgeIcon,
  NoteIcon,
  SettingIcon,
  CardIcon,
  LogoutIcon,
} from '@/componentv2/icons';
import './mobileUserMenu.scss';
import AdminIcon from '@/componentv2/icons/adminIcon';
import { DASHBOARD_URL } from '@/constants/routes';
import { is10DayPass } from '@/contexts/UserProfileContext';

interface UserProfile {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  ispro: boolean;
  isadmin: boolean;
  istrial: boolean;
  issubadmin?: boolean;
  subscription?: {
    productcode?: string;
    productname?: string;
    expiration?: string;
  };
  profilepicture: {
    text?: string;
    xlarge?: string;
  };
}

interface UserRole {
  parent: {
    id: number;
    firstname: string;
    lastname: string;
  };
  role: {
    id: number;
    name: string;
  };
}

interface MobileUserMenuProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile;
  userRoles: UserRole[];
  handleLogout: () => void;
}

const MobileUserMenu: React.FC<MobileUserMenuProps> = ({
  isOpen,
  onClose,
  userProfile,
  userRoles,
  handleLogout,
}) => {
  const handleLogoutClick = () => {
    handleLogout();
    onClose();
    window.location.href = '/index.cfm?go=c.logout';
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="mobile-user-menu-overlay" onClick={onClose} />}

      {/* Mobile User Menu (from right) */}
      <div className={`mobile-user-menu ${isOpen ? 'open' : ''}`}>
        <div className="mobile-user-menu-content">
          <div className="user-popover-header">
            {userProfile.profilepicture?.text ? (
              <div className="user-popover-avatar-text">
                {userProfile.profilepicture.text}
              </div>
            ) : (
              <Avatar
                src={
                  userProfile.profilepicture?.xlarge ||
                  '/n_images/User-logged-in.png'
                }
                userName={`${userProfile.firstname} ${userProfile.lastname}`}
                className="user-popover-avatar"
                rounded
                width={65}
                height={65}
                alt="User Avatar"
              />
            )}
            <div className="user-popover-info">
              <div className="user-name">{`${userProfile.firstname} ${userProfile.lastname}`}</div>
              <div className="user-email">{userProfile.email}</div>
              {userRoles && userRoles.length > 0 && (
                <Link
                  href="/MyAccount/ResetAdminID/1?switch=true"
                  className="switch-account"
                  onClick={onClose}
                >
                  Switch Account
                </Link>
              )}
            </div>
          </div>

          {!userProfile.issubadmin && (
            <div className="user-popover-section">
              <div className="subscription-badge">
                <span className="badge-icon">
                  <BadgeIcon />
                </span>
                <div className="badge-text">
                  <span className="badge-product-name">
                    {is10DayPass(userProfile)
                      ? 'SignUpGenius - 10-Day Access'
                      : userProfile.subscription?.productname
                          ?.replace(/\s*\(\s*(annual|monthly)\s*\)\s*$/i, '')
                          .trim() || 'No Subscription'}
                  </span>
                  {!userProfile.ispro && !userProfile.istrial && (
                    <a
                      href="/index.cfm?go=o.ProGeniusProducts&showtrial=1"
                      className="badge-free-trial"
                    >
                      Try Premium Free
                    </a>
                  )}
                </div>
                <a
                  href="/index.cfm?go=o.mypurchases"
                  className="manage-link"
                  onClick={onClose}
                >
                  Manage
                </a>
              </div>

              {userProfile.ispro &&
                (userProfile.istrial || is10DayPass(userProfile)) && (
                  <div className="subscription-type">Active</div>
                )}
              {userProfile.ispro &&
                !userProfile.istrial &&
                !is10DayPass(userProfile) && (
                  <div className="subscription-type">
                    {userProfile.subscription?.productname
                      ?.toLowerCase()
                      .includes('annual')
                      ? 'Annual'
                      : 'Monthly'}
                  </div>
                )}
            </div>
          )}
          <nav className="user-popover-nav">
            <Link
              href={DASHBOARD_URL}
              className="popover-nav-item"
              onClick={onClose}
            >
              <span className="nav-icon">
                <NoteIcon />
              </span>
              Dashboard
            </Link>
            {!userProfile.issubadmin && (
              <>
                <a
                  href={`/index.cfm?go=m.editprofile&memberid=${userProfile.id}`}
                  className="popover-nav-item"
                  onClick={onClose}
                >
                  <span className="nav-icon">
                    <SettingIcon />
                  </span>
                  Settings & Preferences
                </a>
                <a
                  href="/index.cfm?go=o.mypurchases"
                  className="popover-nav-item"
                  onClick={onClose}
                >
                  <span className="nav-icon">
                    <CardIcon />
                  </span>
                  Billing & Payments
                </a>
                {userProfile.isadmin && (
                  <Link
                    href="/admin"
                    className="popover-nav-item"
                    onClick={onClose}
                  >
                    <span className="nav-icon">
                      <AdminIcon />
                    </span>
                    Admin
                  </Link>
                )}
              </>
            )}
            <div className="user-popover-divider mx-3" />
            <a className="popover-nav-item" onClick={handleLogoutClick}>
              <span className="nav-icon">
                <LogoutIcon />
              </span>
              Log Out
            </a>
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileUserMenu;
